import { Link, router, useForm, usePage } from '@inertiajs/react'
import { useRef, useState, type FormEvent } from 'react'
import SurveyLayout from '@/layouts/SurveyLayout'
import QuestionBuilder from '@/components/QuestionBuilder'
import RichTextEditor, { VariableMenu } from '@/components/RichTextEditor'
import type { SurveyEdit, SharedProps } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface EditProps {
  survey: SurveyEdit
  tab?: string
}

type Tab = 'questions' | 'settings' | 'emails' | 'branding'

const TAB_TITLES: Record<Tab, string> = {
  questions: 'Questions',
  settings: 'Settings',
  emails: 'Email Templates',
  branding: 'Branding',
}

export default function Edit({ survey, tab }: EditProps) {
  const activeTab: Tab = (['questions', 'settings', 'emails', 'branding'].includes(tab || '') ? tab : 'questions') as Tab

  return (
    <SurveyLayout survey={survey}>
      <div className="max-w-3xl mx-auto space-y-6">
        <h2 className="text-2xl font-bold">{TAB_TITLES[activeTab]}</h2>

        {activeTab === 'questions' && <QuestionsTab survey={survey} />}
        {activeTab === 'settings' && <SettingsTab survey={survey} />}
        {activeTab === 'emails' && <EmailTemplatesTab survey={survey} />}
        {activeTab === 'branding' && <BrandingTab survey={survey} />}
      </div>
    </SurveyLayout>
  )
}

/* ----- Questions tab ----- */

function QuestionsTab({ survey }: { survey: SurveyEdit }) {
  return (
    <QuestionBuilder
      surveyId={survey.id}
      questions={survey.questions}
      locked={survey.content_locked}
      scalePresets={survey.scale_presets}
    />
  )
}

/* ----- Settings tab ----- */

function SettingsTab({ survey }: { survey: SurveyEdit }) {
  const ap = useAccountPath()
  const { data, setData, processing, errors } = useForm({
    welcome_message: survey.welcome_message || '',
    closing_message: survey.closing_message || '',
    retrospective_label_before: survey.retrospective_label_before || 'Before',
    retrospective_label_after: survey.retrospective_label_after || 'After',
    anonymity: survey.anonymity || '',
    webhook_url: survey.webhook_url || '',
  })

  // Anonymity locks once responses arrive — backend enforces it; we mirror the
  // disabled state in the UI so it's clear why.
  const anonymityLocked = survey.response_count > 0
  // Retrospective mode is now per question (set in the Questions tab). The
  // column labels below are survey-wide presentation, so they only make sense
  // once at least one question actually asks for a before and an after.
  const retrospectiveQuestions = survey.questions.filter((q) => q.retrospective)
  // Denominated over scale questions only: no other type can ever be retrospective.
  const scaleQuestionCount = survey.questions.filter((q) => q.question_type === 'scale').length
  const isNamed = data.anonymity === 'named'
  // Webhook config is owner/admin-only (server strips webhook_url from member
  // params); hide the field from members so their input isn't silently dropped.
  const { current_membership } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const isAdmin = current_membership?.admin ?? false

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // A real PATCH — router.post with a `_method` override only works with
    // form-encoded bodies (Rack ignores `_method` in JSON), which is why the
    // branding tab pairs it with forceFormData. Without files there's no
    // reason for the override at all.
    router.patch(ap(`/surveys/${survey.id}?tab=settings`), {
      tab: 'settings',
      survey: {
        welcome_message: data.welcome_message,
        closing_message: data.closing_message,
        retrospective_label_before: data.retrospective_label_before,
        retrospective_label_after: data.retrospective_label_after,
        anonymity: data.anonymity || null,
        webhook_url: isNamed ? data.webhook_url : '',
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {Object.keys(errors).length > 0 && (
        <div role="alert" className="alert alert-soft alert-error text-sm">
          <ul className="list-disc list-inside">
            {Object.values(errors).map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Anonymity card */}
      <div className="card bg-base-100">
        <div className="card-body space-y-4">
          <h2 className="text-lg font-semibold">Anonymity</h2>
          <p className="text-sm text-base-content/60">
            Pick how respondents are identified. Locks once the first response is collected.
          </p>

          <div className="space-y-2">
            <label className={`flex items-start gap-3 p-3 border rounded-lg ${data.anonymity === 'anonymous' ? 'border-primary bg-primary/5' : 'border-base-300'} ${anonymityLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="radio"
                className="radio radio-sm radio-primary mt-0.5"
                checked={data.anonymity === 'anonymous'}
                onChange={() => setData('anonymity', 'anonymous')}
                disabled={anonymityLocked}
              />
              <div>
                <span className="font-medium text-sm">Anonymous</span>
                <p className="text-xs text-base-content/60 mt-0.5">
                  No name, no email, no identifiers. Best when you need maximum candor — workshops, classroom check-ins, sensitive feedback.
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 border rounded-lg ${data.anonymity === 'named' ? 'border-primary bg-primary/5' : 'border-base-300'} ${anonymityLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="radio"
                className="radio radio-sm radio-primary mt-0.5"
                checked={data.anonymity === 'named'}
                onChange={() => setData('anonymity', 'named')}
                disabled={anonymityLocked}
              />
              <div>
                <span className="font-medium text-sm">Named</span>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Captures respondent name and email. Required for follow-up, integration with the coaching platform, or any scenario where you need to know who responded. Reports remain aggregated; individual rows are admin-only.
                </p>
              </div>
            </label>
          </div>

          {anonymityLocked && (
            <p className="text-xs text-base-content/50">
              Anonymity is locked because responses have already been collected.
            </p>
          )}

          {isNamed && isAdmin && (
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Webhook URL (optional)</legend>
              <input
                type="url"
                className="input input-sm w-full font-mono text-xs"
                value={data.webhook_url}
                onChange={(e) => setData('webhook_url', e.target.value)}
                placeholder="https://your-platform.com/webhooks/impactcheck"
              />
              <p className="text-xs text-base-content/60 mt-1">
                We'll POST a signed event to this URL when each response is submitted. Used to mark the survey complete in your coaching platform / Matchwell / Customer.io / etc.
              </p>
              {survey.webhook_signing_secret && data.webhook_url && (
                <div className="mt-2 p-2 bg-base-200 rounded text-xs font-mono break-all">
                  <span className="text-base-content/60 select-none">Signing secret:</span>{' '}
                  <span className="select-all">{survey.webhook_signing_secret}</span>
                </div>
              )}
            </fieldset>
          )}
        </div>
      </div>

      {/* Before/after labels card. The MODE itself is no longer set here — it is a
          per-question setting in the Questions tab — so this card owns only the
          column labels, which stay survey-wide. */}
      <div className="card bg-base-100">
        <div className="card-body space-y-4">
          <h2 className="text-lg font-semibold">Before and After Labels</h2>

          {retrospectiveQuestions.length === 0 ? (
            <p className="text-sm text-base-content/60">
              No questions ask for a before and after rating yet. Turn it on for individual scale
              questions in the{' '}
              <Link href={ap(`/surveys/${survey.id}/edit?tab=questions`)} className="link">
                Questions tab
              </Link>
              , and their column labels will appear here.
            </p>
          ) : (
            <>
              <p className="text-sm text-base-content/60">
                {retrospectiveQuestions.length} of {scaleQuestionCount}{' '}
                {scaleQuestionCount === 1 ? 'scale question asks' : 'scale questions ask'} for a
                before and an after rating. These label the two columns respondents see.
              </p>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Column labels</legend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label" htmlFor="retrospective-label-before">"Before" column</label>
                    <input
                      id="retrospective-label-before"
                      type="text"
                      className="input input-sm w-full"
                      value={data.retrospective_label_before}
                      onChange={(e) => setData('retrospective_label_before', e.target.value)}
                      placeholder="Before"
                      disabled={survey.content_locked}
                    />
                  </div>
                  <div>
                    <label className="label" htmlFor="retrospective-label-after">"After" column</label>
                    <input
                      id="retrospective-label-after"
                      type="text"
                      className="input input-sm w-full"
                      value={data.retrospective_label_after}
                      onChange={(e) => setData('retrospective_label_after', e.target.value)}
                      placeholder="After"
                      disabled={survey.content_locked}
                    />
                  </div>
                </div>
              </fieldset>
            </>
          )}

          {survey.content_locked && (
            <p className="text-xs text-base-content/50">
              Labels lock once responses have been collected.
            </p>
          )}
        </div>
      </div>

      {/* Messages card */}
      <div className="card bg-base-100">
        <div className="card-body space-y-4">
          <h2 className="text-lg font-semibold">Survey Messages</h2>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Welcome message</legend>
            <RichTextEditor
              content={data.welcome_message}
              onChange={(html) => setData('welcome_message', html)}
              placeholder="Optional message shown at the top of the survey"
              minHeight="min-h-56"
            />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Closing message</legend>
            <RichTextEditor
              content={data.closing_message}
              onChange={(html) => setData('closing_message', html)}
              placeholder="Optional message shown after submission"
              minHeight="min-h-56"
            />
          </fieldset>
        </div>
      </div>

      {/* Actions outside cards */}
      <div className="flex justify-end gap-2">
        <Link href={ap(`/surveys/${survey.id}`)} className="btn btn-ghost btn-sm">
          Cancel
        </Link>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={processing}
        >
          {processing && <span className="loading loading-spinner loading-sm" />}
          Save changes
        </button>
      </div>
    </form>
  )
}

/* ----- Email templates tab ----- */

interface TemplateFieldState {
  value: string
  dirty: boolean
}

function EmailTemplatesTab({ survey }: { survey: SurveyEdit }) {
  const ap = useAccountPath()
  const [processing, setProcessing] = useState(false)
  const [fields, setFields] = useState<Record<string, TemplateFieldState>>({
    invitation_subject: { value: survey.invitation_subject, dirty: false },
    invitation_body: { value: survey.invitation_body, dirty: false },
    reminder_subject: { value: survey.reminder_subject, dirty: false },
    reminder_body: { value: survey.reminder_body, dirty: false },
  })

  function setField(name: string, value: string) {
    setFields((f) => ({ ...f, [name]: { value, dirty: true } }))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Only send fields the user actually edited — writing a resolved default
    // back would freeze it as a custom override.
    const payload: Record<string, string> = {}
    for (const [name, state] of Object.entries(fields)) {
      if (state.dirty) payload[name] = state.value
    }
    if (Object.keys(payload).length === 0) return
    setProcessing(true)
    router.patch(ap(`/surveys/${survey.id}?tab=emails`), { tab: 'emails', survey: payload }, {
      onFinish: () => setProcessing(false),
    })
  }

  const anyDirty = Object.values(fields).some((f) => f.dirty)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div role="alert" className="alert alert-soft alert-info text-sm">
        <span className="iconify lucide--info size-4" />
        <span>
          These templates are used when you send invitations and reminders from the Respondents page. Each email
          automatically ends with the respondent's personal survey link button.
        </span>
      </div>

      <TemplateCard
        title="Invitation email"
        subtitle="Sent when you invite respondents to the survey."
        subjectField="invitation_subject"
        bodyField="invitation_body"
        fields={fields}
        onChange={setField}
        customized={survey.invitation_subject_custom || survey.invitation_body_custom}
      />

      <TemplateCard
        title="Reminder email"
        subtitle="Sent to people who were invited but haven't completed yet."
        subjectField="reminder_subject"
        bodyField="reminder_body"
        fields={fields}
        onChange={setField}
        customized={survey.reminder_subject_custom || survey.reminder_body_custom}
      />

      <div className="flex justify-end gap-2">
        <Link href={ap(`/surveys/${survey.id}`)} className="btn btn-ghost btn-sm">Cancel</Link>
        <button type="submit" className="btn btn-primary btn-sm" disabled={processing || !anyDirty}>
          {processing && <span className="loading loading-spinner loading-sm" />}
          Save templates
        </button>
      </div>
    </form>
  )
}

interface TemplateCardProps {
  title: string
  subtitle: string
  subjectField: string
  bodyField: string
  fields: Record<string, TemplateFieldState>
  onChange: (name: string, value: string) => void
  customized: boolean
}

function TemplateCard({ title, subtitle, subjectField, bodyField, fields, onChange, customized }: TemplateCardProps) {
  const subjectRef = useRef<HTMLInputElement>(null)
  const subject = fields[subjectField]
  const body = fields[bodyField]

  return (
    <div className="card bg-base-100">
      <div className="card-body space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-base-content/60">{subtitle}</p>
          </div>
          {customized ? (
            <span className="badge badge-soft badge-sm">Customized</span>
          ) : (
            <span className="badge badge-ghost badge-sm">Default</span>
          )}
        </div>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Subject</legend>
          <div className="flex items-center gap-2">
            <input
              ref={subjectRef}
              type="text"
              className="input input-sm w-full"
              value={subject.value}
              onChange={(e) => onChange(subjectField, e.target.value)}
            />
            <VariableMenu onInsert={(token) => {
              const input = subjectRef.current
              const start = input?.selectionStart ?? subject.value.length
              const end = input?.selectionEnd ?? start
              onChange(subjectField, subject.value.slice(0, start) + token + subject.value.slice(end))
            }} />
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Message</legend>
          <RichTextEditor
            content={body.value}
            onChange={(html) => onChange(bodyField, html)}
            minHeight="min-h-40"
            withVariables
          />
        </fieldset>
      </div>
    </div>
  )
}

/* ----- Branding tab ----- */

function BrandingTab({ survey }: { survey: SurveyEdit }) {
  const ap = useAccountPath()
  const { data, setData, processing, errors } = useForm({
    consultancy_name_override: survey.consultancy_name_override || '',
    primary_colour_override: survey.primary_colour_override || '',
    secondary_colour_override: survey.secondary_colour_override || '',
    accent_colour_override: survey.accent_colour_override || '',
    logo_override: null as File | null,
    clear_logo_override: '0',
    client_logo: null as File | null,
    clear_client_logo: '0',
  })

  const [logoPreviewCleared, setLogoPreviewCleared] = useState(false)
  const [clientLogoCleared, setClientLogoCleared] = useState(false)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    router.post(ap(`/surveys/${survey.id}?tab=branding`), {
      _method: 'patch',
      tab: 'branding',
      survey: {
        consultancy_name_override: data.consultancy_name_override || '',
        primary_colour_override: data.primary_colour_override || '',
        secondary_colour_override: data.secondary_colour_override || '',
        accent_colour_override: data.accent_colour_override || '',
        ...(data.logo_override ? { logo_override: data.logo_override } : {}),
        ...(data.clear_logo_override === '1' ? { clear_logo_override: '1' } : {}),
        ...(data.client_logo ? { client_logo: data.client_logo } : {}),
        ...(data.clear_client_logo === '1' ? { clear_client_logo: '1' } : {}),
      },
    }, {
      forceFormData: true,
    })
  }

  function handleClearColour(field: 'primary_colour_override' | 'secondary_colour_override' | 'accent_colour_override') {
    setData(field, '')
  }

  function handleClearLogo() {
    setData('clear_logo_override', '1')
    setData('logo_override', null)
    setLogoPreviewCleared(true)
  }

  function handleClearClientLogo() {
    setData('clear_client_logo', '1')
    setData('client_logo', null)
    setClientLogoCleared(true)
  }

  const showLogoOverride = survey.has_logo_override && !logoPreviewCleared && !data.logo_override
  const showClientLogo = survey.has_client_logo && !clientLogoCleared && !data.client_logo
  const effectivePrimary = data.primary_colour_override || survey.account_primary_colour
  const effectiveSecondary = data.secondary_colour_override || survey.account_secondary_colour
  const effectiveAccent = data.accent_colour_override || survey.account_accent_colour

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {Object.keys(errors).length > 0 && (
        <div role="alert" className="alert alert-soft alert-error text-sm">
          <ul className="list-disc list-inside">
            {Object.values(errors).map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Branding overrides card */}
      <div className="card bg-base-100">
        <div className="card-body space-y-4">
          <h2 className="text-lg font-semibold">Branding Overrides</h2>
          <p className="text-sm text-base-content/60">
            Override your account branding for this survey. Leave blank to use your account defaults.
          </p>

          {/* Consultancy name */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Consultancy name</legend>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={data.consultancy_name_override}
                onChange={(e) => setData('consultancy_name_override', e.target.value)}
                placeholder={survey.account_consultancy_name || 'Account default'}
              />
              {data.consultancy_name_override && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setData('consultancy_name_override', '')}
                  title="Clear override"
                >
                  <span className="iconify lucide--x size-4" />
                </button>
              )}
            </div>
            <p className="label text-xs whitespace-normal">
              {data.consultancy_name_override
                ? 'Using survey override'
                : `Inherited from account: ${survey.account_consultancy_name || 'Not set'}`}
            </p>
          </fieldset>

          {/* Primary colour */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Primary colour</legend>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="size-10 rounded border border-base-300 cursor-pointer p-0.5"
                value={effectivePrimary}
                onChange={(e) => setData('primary_colour_override', e.target.value)}
              />
              <code className="text-sm bg-base-200 px-2 py-1 rounded">
                {effectivePrimary}
              </code>
              {data.primary_colour_override && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleClearColour('primary_colour_override')}
                  title="Revert to account default"
                >
                  <span className="iconify lucide--rotate-ccw size-4" />
                  Revert to Default
                </button>
              )}
            </div>
            <p className="label text-xs whitespace-normal">
              {data.primary_colour_override
                ? 'Using survey override'
                : `Inherited from account: ${survey.account_primary_colour}`}
            </p>
          </fieldset>

          {/* Secondary colour */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Secondary colour</legend>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="size-10 rounded border border-base-300 cursor-pointer p-0.5"
                value={effectiveSecondary}
                onChange={(e) => setData('secondary_colour_override', e.target.value)}
              />
              <code className="text-sm bg-base-200 px-2 py-1 rounded">
                {effectiveSecondary}
              </code>
              {data.secondary_colour_override && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleClearColour('secondary_colour_override')}
                  title="Revert to account default"
                >
                  <span className="iconify lucide--rotate-ccw size-4" />
                  Revert to Default
                </button>
              )}
            </div>
            <p className="label text-xs whitespace-normal">
              {data.secondary_colour_override
                ? 'Using survey override'
                : `Inherited from account: ${survey.account_secondary_colour}`}
            </p>
          </fieldset>

          {/* Accent colour */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Accent colour</legend>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="size-10 rounded border border-base-300 cursor-pointer p-0.5"
                value={effectiveAccent}
                onChange={(e) => setData('accent_colour_override', e.target.value)}
              />
              <code className="text-sm bg-base-200 px-2 py-1 rounded">
                {effectiveAccent}
              </code>
              {data.accent_colour_override && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleClearColour('accent_colour_override')}
                  title="Revert to account default"
                >
                  <span className="iconify lucide--rotate-ccw size-4" />
                  Revert to Default
                </button>
              )}
            </div>
            <p className="label text-xs whitespace-normal">
              {data.accent_colour_override
                ? 'Using survey override'
                : `Inherited from account: ${survey.account_accent_colour}`}{' '}
              — highlights the average marker on report charts
            </p>
          </fieldset>

          {/* Logo override */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Consultancy logo</legend>

            {/* Resolved logo preview — shows the logo this survey will ACTUALLY
                send, whether that's a survey override or the inherited account
                logo. Previously only an override rendered an image, so a survey
                on the account default showed nothing at all (feedback #13) and
                there was no way to eyeball what branding a duplicated survey had
                carried over (D4). */}
            {(showLogoOverride || (!data.logo_override && survey.account_logo_url)) && (
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={(showLogoOverride ? survey.logo_url : survey.account_logo_url) || ''}
                  alt={showLogoOverride ? 'Survey logo override' : 'Account logo'}
                  className="h-12 max-w-32 object-contain rounded border border-base-300 p-1"
                />
                <span className="badge badge-soft badge-sm">
                  {showLogoOverride ? 'This survey' : 'Account default'}
                </span>
                {showLogoOverride && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm text-error"
                    onClick={handleClearLogo}
                    title="Remove logo override"
                  >
                    <span className="iconify lucide--trash-2 size-4" />
                    Remove
                  </button>
                )}
              </div>
            )}

            {/* File input */}
            <input
              type="file"
              className="file-input file-input-sm w-full"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                setData('logo_override', file)
                if (file) {
                  setData('clear_logo_override', '0')
                  setLogoPreviewCleared(false)
                }
              }}
            />
            <p className="label text-xs whitespace-normal">
              {showLogoOverride
                ? 'Using survey logo override'
                : data.logo_override
                  ? `New file selected: ${data.logo_override.name}`
                  : survey.account_logo_url
                    ? 'No override \u2014 using account logo'
                    : 'No logo set on account or survey'}
            </p>
          </fieldset>

          {/* Client / programme logo */}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Client logo</legend>
            <p className="text-sm text-base-content/60">
              Shown next to your logo on this survey and its report (co-branding).
            </p>

            {showClientLogo && (
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={survey.client_logo_url || ''}
                  alt="Current client logo"
                  className="h-12 max-w-32 object-contain rounded border border-base-300 p-1"
                />
                <span className="badge badge-soft badge-sm">This survey</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-error"
                  onClick={handleClearClientLogo}
                  title="Remove client logo"
                >
                  <span className="iconify lucide--trash-2 size-4" />
                  Remove
                </button>
              </div>
            )}

            <input
              type="file"
              className="file-input file-input-sm w-full"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                setData('client_logo', file)
                if (file) {
                  setData('clear_client_logo', '0')
                  setClientLogoCleared(false)
                }
              }}
            />
            <p className="label text-xs whitespace-normal">
              {showClientLogo
                ? 'Using client logo'
                : data.client_logo
                  ? `New file selected: ${data.client_logo.name}`
                  : 'No client logo set'}
            </p>
          </fieldset>
        </div>
      </div>

      {/* Actions outside cards */}
      <div className="flex justify-end gap-2">
        <Link href={ap(`/surveys/${survey.id}`)} className="btn btn-ghost btn-sm">
          Cancel
        </Link>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={processing}
        >
          {processing && <span className="loading loading-spinner loading-sm" />}
          Save changes
        </button>
      </div>
    </form>
  )
}


